import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Upload,
} from "antd";
import { addDoc, collection, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { PRODUCT_STOCK_COUNTRIES } from "../../../../../constants/ProductConstant";
import i18n from "../../../../../translations/i18n";
import TextArea from "antd/es/input/TextArea";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import dayjs from "dayjs";
import { FIRESTORE_LEADS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../../constants/FirestoreConstant";
import { firestore, storage } from "../../../../../configs/FirebaseConfig";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { formatPhoneNumber } from "../../../../../functions/formatters";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import { LEAD_DEFAULT_STATUS } from "../../../../../constants/LeadConstant";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  findDeliveryZoneByPostalCode,
  getDeliveryZoneStatus,
  sanitizePostalCode,
} from "../../../../../functions/deliveryZones";

export default function AddLeadModal({ visible, onClose, mode = "add", initialValues = null, allLeads = [] }) {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [companyTypeOptions, setCompanyTypeOptions] = useState([]);
  const [originOptions, setOriginOptions] = useState([]);

  const isEditMode = mode === "edit";
  const countryValue = Form.useWatch("country", form);
  const deliveryLabelValue = Form.useWatch("deliveryLabel", form);
  const zipCodeValue = Form.useWatch("zipCode", form);
  const [deliveryZone, setDeliveryZone] = useState(null);
  const { getCountryDelivery, getCountryDeliveryLabels } = useCountries();
  const defaultDeliverySettings = getCountryDelivery(countryValue || COMPANY_COUNTRY_CODE);
  const [deliveryInstructionsFile, setDeliveryInstructionsFile] = useState(null);
  const [currentDeliveryInstructionsUrl, setCurrentDeliveryInstructionsUrl] = useState(null);
  const [currentDeliveryInstructionsPath, setCurrentDeliveryInstructionsPath] = useState(null);

  // Normalize timeslot to JSON with consistent key order to prevent display mismatches
  const stringifyTimeSlot = (slot) =>
    JSON.stringify({ start: slot.start, end: slot.end, deliverySurcharge: Number(slot.deliverySurcharge || 0) });

  // Extract unique company types and origins from all leads
  useEffect(() => {
    if (allLeads && allLeads.length > 0) {
      const uniqueCompanyTypes = [
        ...new Set(allLeads.map((lead) => lead.companyType).filter((type) => type && type.trim() !== "")),
      ].sort();

      setCompanyTypeOptions(
        uniqueCompanyTypes.map((type) => ({
          value: type,
          label: type,
        })),
      );

      const uniqueOrigins = [
        ...new Set(allLeads.map((lead) => lead.origin).filter((origin) => origin && origin.trim() !== "")),
      ].sort();

      setOriginOptions(
        uniqueOrigins.map((origin) => ({
          value: origin,
          label: origin,
        })),
      );
    }
  }, [allLeads]);

  // Initialize form when initialValues change
  useEffect(() => {
    if (visible && initialValues && isEditMode) {
      const formValues = {
        companyName: initialValues.companyName,
        contactFirstName: initialValues.contactFirstName || "",
        contactLastName: initialValues.contactLastName || "",
        email: initialValues.email,
        phone: initialValues.phone,
        street: initialValues.street,
        houseNumber: initialValues.houseNumber,
        houseNumberAddition: initialValues.houseNumberAddition || "",
        zipCode: initialValues.zipCode,
        city: initialValues.city,
        country: initialValues.country || PRODUCT_STOCK_COUNTRIES[0].code,
        website: initialValues.website || "",
        companyType: initialValues.companyType || "",
        notes: initialValues.notes || "",
        origin: initialValues.origin || "",
        deliveryLabel: initialValues.deliveryLabel || null,
        deliveryTimeSlot: initialValues.deliveryTimeSlot ? stringifyTimeSlot(initialValues.deliveryTimeSlot) : null,
        deliveryInstructions: initialValues.deliveryInstructions || "",
      };

      form.setFieldsValue(formValues);

      // Set delivery instructions file state
      setDeliveryInstructionsFile(null);
      setCurrentDeliveryInstructionsUrl(initialValues?.deliveryInstructionsUrl || null);
      setCurrentDeliveryInstructionsPath(initialValues?.deliveryInstructionsPath || null);
    } else if (visible && !isEditMode) {
      // Reset file state for add mode
      setDeliveryInstructionsFile(null);
      setCurrentDeliveryInstructionsUrl(null);
      setCurrentDeliveryInstructionsPath(null);
    }
  }, [visible, initialValues, isEditMode, form]);

  // Auto-populate time slot when delivery label changes
  useEffect(() => {
    if (!deliveryLabelValue || !defaultDeliverySettings?.deliveryLabels) return;

    const labelConfig = defaultDeliverySettings.deliveryLabels.find((dl) => dl.deliveryLabel === deliveryLabelValue);

    if (labelConfig) {
      // Only auto-set in ADD mode or if lead doesn't have existing value
      if (!isEditMode || !initialValues?.deliveryTimeSlot) {
        if (labelConfig.deliveryTimeSlots?.length > 0) {
          form.setFieldValue("deliveryTimeSlot", stringifyTimeSlot(labelConfig.deliveryTimeSlots[0]));
        }
      }
    }
  }, [deliveryLabelValue, defaultDeliverySettings, isEditMode, initialValues, form]);

  // Auto-detect delivery zone for French postal codes
  useEffect(() => {
    if (countryValue === "FR" && zipCodeValue) {
      const sanitized = sanitizePostalCode(zipCodeValue);
      const countryConfig = getCountryDelivery(countryValue);
      const zone = findDeliveryZoneByPostalCode(sanitized, countryConfig);
      setDeliveryZone(zone);
    } else {
      setDeliveryZone(null);
    }
  }, [zipCodeValue, countryValue, getCountryDelivery]);

  const handleSubmit = () => {
    form.validateFields().then(async (values) => {
      setIsLoading(true);

      try {
        let leadIdForUpload = isEditMode ? initialValues.id : null;
        const address =
          values.street.trim() +
          " " +
          String(values.houseNumber).trim() +
          (values.houseNumberAddition ? " " + values.houseNumberAddition.trim() : "") +
          ", " +
          values.city.trim() +
          ", " +
          String(values.zipCode).trim();

        // Parse delivery time slot safely
        let deliveryTimeSlotObject = null;
        if (values.deliveryTimeSlot) {
          try {
            deliveryTimeSlotObject = JSON.parse(values.deliveryTimeSlot);
          } catch (e) {
            console.error("Error parsing time slot:", e);
            deliveryTimeSlotObject = null;
          }
        }

        const obj = {
          companyName: values.companyName.trim(),
          contactFirstName: values.contactFirstName?.trim() || null,
          contactLastName: values.contactLastName?.trim() || null,
          email: values.email ? values.email.toLowerCase().trim() : null,
          phone: values.phone ? formatPhoneNumber(values.phone) : null,
          street: values.street.trim(),
          houseNumber: String(values.houseNumber).trim(),
          houseNumberAddition: values.houseNumberAddition?.trim() || null,
          zipCode: String(values.zipCode).trim(),
          city: values.city.trim(),
          country: values.country,
          address: address,
          region: COMPANY_COUNTRY_CODE,
          website: values.website?.trim() || null,
          companyType: values.companyType?.trim() || null,
          notes: values.notes?.trim() || null,
          origin: values.origin?.trim() || null,
          deliveryLabel: values.deliveryLabel || null,
          deliveryTimeSlot: deliveryTimeSlotObject,
          deliveryInstructions: values.deliveryInstructions?.trim() || null,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        };

        if (isEditMode) {
          // Edit mode: Update existing lead
          let deliveryInstructionsUrl = currentDeliveryInstructionsUrl || null;
          let deliveryInstructionsPath = currentDeliveryInstructionsPath || null;

          if (deliveryInstructionsFile && leadIdForUpload) {
            const storagePath = `delivery-instructions/leads/${leadIdForUpload}/delivery-instructions.pdf`;
            const storageRef = ref(storage, storagePath);

            try {
              await uploadBytes(storageRef, deliveryInstructionsFile);
              const downloadUrl = await getDownloadURL(storageRef);
              deliveryInstructionsUrl = downloadUrl;
              deliveryInstructionsPath = storagePath;

              if (currentDeliveryInstructionsPath && currentDeliveryInstructionsPath !== storagePath) {
                deleteObject(ref(storage, currentDeliveryInstructionsPath)).catch(console.error);
              }
            } catch (uploadError) {
              console.error("Delivery Instructions upload error:", uploadError);
              message.error("Upload failed: Delivery Instructions");
              setIsLoading(false);
              return;
            }
          }

          const objWithDeliveryInstructions = {
            ...obj,
            deliveryInstructionsUrl,
            deliveryInstructionsPath,
          };

          await updateDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, initialValues.id),
            objWithDeliveryInstructions,
          );
          message.success(t("notifications.lead_updated"));
          closeModal();
        } else {
          // Add mode: Check for duplicate email first
          if (obj.email) {
            const emailCheckQuery = query(
              collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE),
              where("email", "==", obj.email),
            );
            const emailCheckSnapshot = await getDocs(emailCheckQuery);

            if (!emailCheckSnapshot.empty) {
              message.error(t("notifications.lead_email_exists"), 5);
              setIsLoading(false);
              return;
            }
          }

          // Check for duplicate phone number
          if (obj.phone) {
            const phoneCheckQuery = query(
              collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE),
              where("phone", "==", obj.phone),
            );
            const phoneCheckSnapshot = await getDocs(phoneCheckQuery);

            if (!phoneCheckSnapshot.empty) {
              message.error(t("notifications.lead_phone_exists"), 5);
              setIsLoading(false);
              return;
            }
          }

          // Create new lead
          obj.createdAt = dayjs().unix();
          obj.createdBy = email;
          obj.status = LEAD_DEFAULT_STATUS;

          const docRef = await addDoc(
            collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE),
            obj,
          );
          const leadId = docRef.id;
          leadIdForUpload = leadId;
          await updateDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, docRef.id),
            { leadId },
          );

          // Upload delivery instructions file if provided
          if (deliveryInstructionsFile && leadIdForUpload) {
            const storagePath = `delivery-instructions/leads/${leadIdForUpload}/delivery-instructions.pdf`;
            const storageRef = ref(storage, storagePath);

            try {
              await uploadBytes(storageRef, deliveryInstructionsFile);
              const downloadUrl = await getDownloadURL(storageRef);

              await updateDoc(
                doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, leadIdForUpload),
                {
                  deliveryInstructionsUrl: downloadUrl,
                  deliveryInstructionsPath: storagePath,
                },
              );
            } catch (uploadError) {
              console.error("Delivery Instructions upload error:", uploadError);
              message.warning("Lead created but file upload failed");
            }
          }

          message.success(t("notifications.lead_added"));
          closeModal();
        }
      } catch (err) {
        console.error(err);
        message.error(t("notifications.error_saving_lead"), 5);
      } finally {
        setIsLoading(false);
      }
    });
  };

  const closeModal = () => {
    form.resetFields();
    setDeliveryInstructionsFile(null);
    setCurrentDeliveryInstructionsUrl(null);
    setCurrentDeliveryInstructionsPath(null);
    onClose();
  };

  const handleRemoveDeliveryInstructions = async () => {
    if (!initialValues?.id) return;
    try {
      if (currentDeliveryInstructionsPath) {
        await deleteObject(ref(storage, currentDeliveryInstructionsPath));
      } else if (currentDeliveryInstructionsUrl) {
        await deleteObject(ref(storage, currentDeliveryInstructionsUrl));
      }
    } catch (e) {
      console.error(e);
    }

    try {
      await updateDoc(
        doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, initialValues.id),
        {
          deliveryInstructionsUrl: null,
          deliveryInstructionsPath: null,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        },
      );
      setCurrentDeliveryInstructionsUrl(null);
      setCurrentDeliveryInstructionsPath(null);
      setDeliveryInstructionsFile(null);
      message.success("Delivery Instructions removed");
    } catch (e) {
      console.error(e);
      message.error("Failed to remove Delivery Instructions");
    }
  };

  const testUsers = [
    {
      id: "twan",
      label: "Twan",
      houseNumber: "18",
      email: "twan@growy.nl",
      contactFirstName: "Twan",
      contactLastName: "Schmitz",
      zipCode: "1041 AT",
      city: "Amsterdam",
      companyName: "Test Lead - Twan",
      houseNumberAddition: "",
      country: "NL",
      street: "Ankerweg",
      phone: "+31641252480",
      origin: "Website",
    },
    {
      id: "iwan",
      label: "Iwan",
      houseNumber: "18",
      email: "iwan@digitalkiss.nl",
      contactFirstName: "Iwan",
      contactLastName: "de Visser",
      zipCode: "1065 GN",
      city: "Amsterdam",
      companyName: "Test Lead - Iwan",
      houseNumberAddition: "1",
      country: "NL",
      street: "Esther de Boer-van Rijkstraat",
      phone: "+27631433397",
      origin: "Website",
    },
    {
      id: "sam",
      label: "Sam",
      houseNumber: "3",
      email: "leendert@digitalkiss.nl",
      contactFirstName: "Leendert",
      contactLastName: "Eloff",
      zipCode: "1601 SL",
      city: "Enkhuizen",
      companyName: "Test Lead - Sam",
      houseNumberAddition: "",
      country: "NL",
      street: "Noorder Boerenvaart",
      phone: "+31644541305",
      origin: "Website",
    },
    {
      id: "leendert",
      label: "Leendert",
      houseNumber: "18",
      email: "leendert@digitalkiss.nl",
      contactFirstName: "Leendert",
      contactLastName: "Eloff",
      zipCode: "1065 GN",
      city: "Amsterdam",
      companyName: "Test Lead - Leendert",
      houseNumberAddition: "1",
      country: "NL",
      street: "Esther de Boer-van Rijkstraat",
      phone: "+31644541305",
      origin: "Website",
    },
  ];

  const handleTestUserSelect = (userId) => {
    const selectedUser = testUsers.find((user) => user.id === userId);
    if (selectedUser) {
      const { id, label, ...userData } = selectedUser;
      form.setFieldsValue(userData);
    }
  };

  // Custom wrapper voor PhoneInput om te werken met Ant Design Form
  const PhoneInputWrapper = ({ value, onChange }) => {
    return (
      <PhoneInput
        international
        defaultCountry="NL"
        countries={["NL", "BE", "FR", "DE"]}
        countryCallingCodeEditable={false}
        value={value}
        onChange={onChange}
        className="ant-input"
        style={{
          height: "32px",
          padding: "4px 11px",
          fontSize: "14px",
        }}
      />
    );
  };

  return (
    <Modal
      title={isEditMode ? t("edit_lead", { lead: initialValues?.companyName || "" }) : t("add_lead")}
      open={visible}
      onOk={handleSubmit}
      onCancel={closeModal}
      okText={t("save")}
      cancelText={t("close")}
      okButtonProps={{ loading: isLoading, htmlType: "submit" }}
      centered
      width={800}
      zIndex={1100}
      footer={[
        <Button key="cancel" onClick={closeModal}>
          {t("close")}
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={handleSubmit}>
          {t("save")}
        </Button>,
      ]}
    >
      {!isEditMode && process.env.NODE_ENV !== "production" && (
        <div style={{ backgroundColor: "#f0f2f5", borderRadius: "4px", padding: "12px", marginBottom: 16 }}>
          <p>-- Alleen zichtbaar op DEV omgeving --</p>
          <div style={{ marginBottom: 16, padding: "12px", backgroundColor: "#f0f2f5", borderRadius: "4px" }}>
            <Select
              placeholder="Select a test user"
              style={{ width: "100%" }}
              onChange={handleTestUserSelect}
              options={testUsers.map((user) => ({
                value: user.id,
                label: user.label,
              }))}
            />
          </div>
        </div>
      )}
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          country: PRODUCT_STOCK_COUNTRIES[0].code,
        }}
      >
        {/* Basic Company Info */}
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name="companyName"
              label={t("company_name")}
              rules={[{ required: true, message: t("form.enter_company_name") }]}
            >
              <Input placeholder={t("company_name")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name="contactFirstName" label={t("contact_first_name")}>
              <Input placeholder={t("contact_first_name")} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="contactLastName" label={t("contact_last_name")}>
              <Input placeholder={t("contact_last_name")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="email"
              label={t("email")}
              rules={[
                {
                  type: "email",
                  message: t("form.enter_valid_email"),
                },
              ]}
            >
              <Input placeholder={t("email")} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name="phone"
              label={t("phone")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_phone"),
                },
              ]}
            >
              <PhoneInputWrapper />
            </Form.Item>
          </Col>
        </Row>

        {/* Address */}
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              name="street"
              label={t("street")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_street"),
                },
              ]}
            >
              <Input placeholder={t("street")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              name="zipCode"
              label={
                <span>
                  {t("zip_code")}
                  {countryValue === "FR" && deliveryZone && (
                    <Tag color={getDeliveryZoneStatus(deliveryZone).color} style={{ marginLeft: 8 }}>
                      {deliveryZone.deliveryTime}
                    </Tag>
                  )}
                  {countryValue === "FR" && zipCodeValue && !deliveryZone && (
                    <Tag color="orange" style={{ marginLeft: 8 }}>
                      {t("delivery_zone_unknown")}
                    </Tag>
                  )}
                </span>
              }
              rules={[
                {
                  required: true,
                  message: t("form.enter_zip_code"),
                },
              ]}
            >
              <Input placeholder={t("zip_code")} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item
              name="houseNumber"
              label={t("house_number")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_house_number"),
                },
              ]}
            >
              <Input placeholder={t("house_number")} />
            </Form.Item>
          </Col>
          <Col xs={12} lg={6}>
            <Form.Item name="houseNumberAddition" label={t("addition")}>
              <Input placeholder={t("addition")} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={12}>
            <Form.Item
              name="city"
              label={t("city")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_city"),
                },
              ]}
            >
              <Input placeholder={t("city")} />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name="country"
              label={t("country")}
              rules={[
                {
                  required: true,
                  message: t("form.enter_country"),
                },
              ]}
            >
              <Select className="mr-2">
                {PRODUCT_STOCK_COUNTRIES.map((country) => (
                  <Select.Option key={country.code} value={country.code}>
                    {i18n.t(country.label)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name="origin" label={t("origin")}>
              <AutoComplete
                placeholder={t("select_or_enter_origin")}
                allowClear
                options={originOptions}
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Delivery Settings */}
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name="deliveryLabel" label={t("delivery_label")}>
              <Select style={{ width: "100%" }} placeholder={t("delivery_label_placeholder")} allowClear>
                {getCountryDeliveryLabels(countryValue).map((deliveryLabel) => (
                  <Select.Option key={deliveryLabel.code} value={deliveryLabel.code}>
                    {deliveryLabel.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="deliveryTimeSlot" label={t("delivery_time_slot")}>
              <Select
                style={{ width: "100%" }}
                placeholder={t("select_delivery_time_slot")}
                disabled={!deliveryLabelValue}
                allowClear
              >
                {deliveryLabelValue &&
                  defaultDeliverySettings?.deliveryLabels
                    ?.find((dl) => dl.deliveryLabel === deliveryLabelValue)
                    ?.deliveryTimeSlots?.map((slot, index) => (
                      <Select.Option key={index} value={stringifyTimeSlot(slot)}>
                        {slot.start} - {slot.end}
                        {slot.deliverySurcharge > 0 && ` (+€${slot.deliverySurcharge})`}
                      </Select.Option>
                    ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Extra Information */}
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name="website" label={t("website")}>
              <Input placeholder={t("website")} />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="companyType" label={t("company_type")}>
              <AutoComplete
                placeholder={t("select_or_enter_company_type")}
                allowClear
                options={companyTypeOptions}
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item name="deliveryInstructions" label={t("delivery_instructions")}>
              <TextArea placeholder={t("delivery_notes_placeholder")} rows={2} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item label="Delivery Instructions (PDF)">
              <Space direction="vertical" style={{ width: "100%" }}>
                {!!currentDeliveryInstructionsUrl && (
                  <a href={currentDeliveryInstructionsUrl} target="_blank" rel="noreferrer">
                    Delivery Instructions (PDF)
                  </a>
                )}
                {!currentDeliveryInstructionsUrl && (
                  <Upload
                    accept="application/pdf"
                    maxCount={1}
                    showUploadList={{ showRemoveIcon: false }}
                    beforeUpload={(file) => {
                      const isPdf = file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf");
                      if (!isPdf) {
                        message.error("Only PDF files are allowed");
                        return Upload.LIST_IGNORE;
                      }
                      setDeliveryInstructionsFile(file);
                      return false;
                    }}
                  >
                    <Button>Upload PDF</Button>
                  </Upload>
                )}
                {!!currentDeliveryInstructionsUrl && isEditMode && (
                  <Popconfirm
                    title="Remove Delivery Instructions?"
                    okText={t("delete")}
                    cancelText={t("close")}
                    onConfirm={handleRemoveDeliveryInstructions}
                  >
                    <Button danger>Remove</Button>
                  </Popconfirm>
                )}
              </Space>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col xs={24}>
            <Form.Item name="notes" label={t("internal_notes")}>
              <TextArea placeholder={t("internal_notes_placeholder")} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
